import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'
import Select from 'react-select'

import {
  capitalizeString,
  createWorkbook,
  saveWorkbook,
  setColumnsAndRows,
  setHeaders,
  addWorksheet,
  DEFAULTNUMHEADERS,
  currencyFormat,
  numberFormat,
  percentFormat,
  formatCurrency,
  formatNumber,
} from '../../utilities/helpers'
import {
  getOrgEventsForReport,
  getPaymentListingsV4,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { DateRangeInputWrapper } from '../DateInputWrapper'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'
import { dateTime, constants } from '@blocktickets/shared-utils'

import cardIcon from '../../assets/icons/card.svg'
import './paymentMethodTypeReport.scss'

const {
  ORDER_STATUS,
  ORDER_STATUS_LABEL,
  ORDER_SOURCE,
  ORDER_SOURCE_LABEL,
  PRODUCT_TYPE,
  PRODUCT_TYPE_LABEL,
  PAYMENT_TYPE,
} = constants

const REMOVED_ORDER_STATUS = [
  ORDER_STATUS.COMPLETE_FROM_ASSIGNMENT,
  ORDER_STATUS.COMPLETE_FROM_EXCHANGE,
  ORDER_STATUS.COMPLETE_FROM_GROUP_PURCHASE,
  ORDER_STATUS.COMPLETE_FROM_INVOICE,
  ORDER_STATUS.COMPLETE_FROM_TRANSFER,
  ORDER_STATUS.DRAFT,
  ORDER_STATUS.INCOMPLETE,
  ORDER_STATUS.INVOICE_SENT,
  ORDER_STATUS.PENDING_SEND,
  ORDER_STATUS.FAILED,
  ORDER_STATUS.DUPLICATE_ORDER,
  ORDER_STATUS.DUPLICATE,
]

const ORDER_STATUS_OPTIONS = Object.values(ORDER_STATUS)
  .filter((status) => !REMOVED_ORDER_STATUS.includes(status))
  .map((status) => ({
    value: status,
    label:
      ORDER_STATUS_LABEL[status] || capitalizeString(status.replace(/_/g, ' ')),
  }))

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All' },
  ...Object.values(ORDER_SOURCE).map((source) => ({
    value: source,
    label: ORDER_SOURCE_LABEL[source] || capitalizeString(source),
  })),
]

const PRODUCT_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  ...Object.values(PRODUCT_TYPE).map((type) => ({
    value: type,
    label: PRODUCT_TYPE_LABEL[type] || capitalizeString(type),
  })),
]

const formatBrandLabel = (brand = '') => {
  const cleaned = String(brand).replace(/_/g, ' ').trim()
  return cleaned ? capitalizeString(cleaned) : 'Unknown'
}

const ExcelIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
)

export default function PaymentMethodTypeReportWrapper({ organization }) {
  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [paymentListings, setPaymentListings] = useState(null)
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [events, setEvents] = useState([])
  const [selectedEventUuid, setSelectedEventUuid] = useState('all')
  const [selectedStatuses, setSelectedStatuses] = useState([
    ORDER_STATUS_OPTIONS.find((s) => s.value === 'complete'),
  ])
  const [selectedSource, setSelectedSource] = useState('all')
  const [selectedProductType, setSelectedProductType] = useState('all')

  useEffect(() => {
    const fetchEvents = async () => {
      if (!organization?.uuid) return
      try {
        const res = await getOrgEventsForReport(organization.uuid)
        setEvents(res.data || [])
      } catch (error) {
        console.error('Error fetching events:', error)
      }
    }
    fetchEvents()
  }, [organization?.uuid])

  useEffect(() => {
    if (
      selectedProductType === PRODUCT_TYPE.FLEX_PACKAGES ||
      selectedProductType === PRODUCT_TYPE.SEASON_PACKAGES
    ) {
      setSelectedEventUuid('all')
    }
  }, [selectedProductType])

  useEffect(() => {
    const fetchData = async () => {
      if (!fromDate || !toDate) return

      setIsLoadingData(true)

      try {
        const tz = dateTime.getBrowserTimezone('UTC')
        const pad = (n) => String(n).padStart(2, '0')
        const ymd = (d) =>
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

        const startLocalIso = `${ymd(fromDate)}T00:00:00.000`
        const endLocalIso = `${ymd(toDate)}T23:59:59.999`

        const fromUtcIso = dateTime.localToUtc(startLocalIso, tz).toISOString()
        const toUtcIso = dateTime.localToUtc(endLocalIso, tz).toISOString()

        const res = await getPaymentListingsV4(
          organization.uuid,
          fromUtcIso,
          toUtcIso,
          selectedEventUuid,
          selectedStatuses.map((s) => s.value),
          selectedSource,
          PAYMENT_TYPE.CARD,
          selectedProductType
        )

        setPaymentListings(res.data || [])
      } catch (error) {
        console.error('Error fetching payment listings v4:', error)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    fetchData()
  }, [
    fromDate,
    toDate,
    organization.uuid,
    selectedEventUuid,
    selectedStatuses,
    selectedSource,
    selectedProductType,
    refreshToken,
  ])

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates
    setFromDate(start)
    setToDate(end)
  }

  const cardBrandRows = useMemo(() => {
    if (!paymentListings || paymentListings.length === 0) {
      return { rows: [], totalCount: 0 }
    }

    const totals = {}
    let totalCount = 0

    paymentListings.forEach((listing) => {
      if (listing?.paymentType !== PAYMENT_TYPE.CARD) return
      const rawBrand = listing?.paymentMethod
      const normalized = String(rawBrand || '')
        .trim()
        .toLowerCase()
      if (!normalized) return

      totals[normalized] = totals[normalized] || {
        brand: formatBrandLabel(rawBrand),
        total: 0,
        totalRevenue: 0,
      }
      totals[normalized].total += 1
      totals[normalized].totalRevenue += Number(listing?.gross || 0)
      totalCount += 1
    })

    const rows = Object.values(totals)
      .map((row) => ({
        ...row,
        percentage: totalCount ? (row.total / totalCount) * 100 : 0,
      }))
      .sort((a, b) =>
        b.total !== a.total ? b.total - a.total : a.brand.localeCompare(b.brand)
      )

    return { rows, totalCount }
  }, [paymentListings])

  const downloadReport = async () => {
    try {
      if (!cardBrandRows.rows || cardBrandRows.rows.length === 0) return

      const dateRange = `${moment(fromDate).format('MMM D, YYYY')} - ${moment(
        toDate
      ).format('MMM D, YYYY')}`

      const workbook = createWorkbook()
      const worksheet = addWorksheet('Card Brand Breakdown', workbook)

      const headerLines = [`Date Range: ${dateRange}`]
      setHeaders(
        'Card Brand Breakdown',
        organization?.name || '',
        worksheet,
        headerLines
      )

      const keys = ['brand', 'total', 'totalRevenue', 'percentageUsed']
      const formatRow = (row) => ({
        brand: row.brand,
        total: row.total,
        totalRevenue: row.totalRevenue,
        percentageUsed: row.percentage / 100,
      })

      setColumnsAndRows(keys, cardBrandRows.rows, formatRow, worksheet)

      const headerRows = DEFAULTNUMHEADERS + headerLines.length
      const columnHeaderRowNumber = headerRows
      const headerRow = worksheet.getRow(columnHeaderRowNumber)
      ;['Brand', 'Total', 'Total Revenue', 'Percentage Used'].forEach(
        (label, idx) => {
          headerRow.getCell(idx + 1).value = label
        }
      )
      headerRow.commit?.()

      numberFormat(2, worksheet, headerRows)
      currencyFormat(3, worksheet, headerRows)
      percentFormat(4, worksheet, headerRows)

      await saveWorkbook(workbook, 'card brand breakdown')
    } catch (error) {
      console.error('Excel export failed:', error)
    }
  }

  return (
    <section className="spacer-sm card-brand-report">
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Card Brand Breakdown</h1>
        </div>
        <p className="section-header-desc">
          Orders paid by card with a recorded payment method type, grouped by
          brand.
        </p>
      </header>

      <div className="payment-listings-v4-filters mb-4">
        <Stack gap={3}>
          <Stack direction="horizontal" gap={3} className="align-items-end">
            <div style={{ width: '400px' }}>
              <Form.Group controlId="dateRangeGroup">
                <Form.Label className="uppercase text-body-xs font-weight-bold">
                  Date Range
                </Form.Label>
                <DateRangeInputWrapper
                  id="dateRange"
                  startDate={fromDate}
                  endDate={toDate}
                  onChange={handleDateRangeChange}
                  isReadOnly={isLoadingData}
                  size="sm"
                  styles="form-control-height"
                />
              </Form.Group>
            </div>

            <Form.Group controlId="productTypeSelect" style={{ flex: 1 }}>
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Product Type
              </Form.Label>
              <Form.Select
                className="form-control-height"
                value={selectedProductType}
                onChange={(e) => setSelectedProductType(e.target.value)}
                disabled={isLoadingData}
              >
                {PRODUCT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="eventSelect" style={{ flex: 2 }}>
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Select Event
              </Form.Label>
              <Form.Select
                size="sm"
                className="form-control-height"
                value={selectedEventUuid}
                onChange={(e) => setSelectedEventUuid(e.target.value)}
                disabled={
                  isLoadingData ||
                  selectedProductType === PRODUCT_TYPE.FLEX_PACKAGES ||
                  selectedProductType === PRODUCT_TYPE.SEASON_PACKAGES
                }
              >
                <option value="all">All</option>
                {events.map((event) => (
                  <option key={event.uuid} value={event.uuid}>
                    {event.name}
                    {event.start &&
                      ` - ${moment(event.start).format('MMM D, YYYY')}`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="sourceSelect" style={{ flex: 1 }}>
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Source
              </Form.Label>
              <Form.Select
                className="form-control-height"
                value={selectedSource}
                onChange={(e) => setSelectedSource(e.target.value)}
                disabled={isLoadingData}
              >
                {SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Stack>

          <Stack direction="horizontal" gap={3} className="align-items-end">
            <Form.Group controlId="statusSelect" className="flex-grow-1">
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Order Status
              </Form.Label>
              <Select
                isMulti
                name="statuses"
                options={ORDER_STATUS_OPTIONS}
                className="react-select-container"
                classNamePrefix="react-select"
                value={selectedStatuses}
                onChange={setSelectedStatuses}
                disabled={isLoadingData}
                placeholder="All Statuses"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '47.59px',
                    fontSize: '14px',
                    backgroundColor: '#fcfcfd',
                    borderWidth: '2px',
                    borderColor: state.isFocused ? '#3e8bf7' : '#e6e8ec',
                    borderRadius: '12px',
                    '&:hover': {
                      borderColor: state.isFocused ? '#3e8bf7' : '#e6e8ec',
                    },
                    boxShadow: state.isFocused
                      ? '0 0 0 0.25rem rgba(62, 139, 247, 0.25)'
                      : null,
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 8px',
                  }),
                  input: (base) => ({
                    ...base,
                    margin: 0,
                    padding: 0,
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: '45.59px',
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#eef5ff',
                    borderRadius: '20px',
                    padding: '2px 8px',
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#3e8bf7',
                    fontSize: '13px',
                    fontWeight: '500',
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: '#3e8bf7',
                    ':hover': {
                      backgroundColor: 'transparent',
                      color: '#3e8bf7',
                    },
                  }),
                }}
              />
            </Form.Group>

            <ReportRefreshButton
              onRefresh={refresh}
              isRefreshing={isLoadingData}
              disabled={isLoadingData || !fromDate || !toDate}
            />

            <button
              type="button"
              className="btn btn-outline-light icon-button form-control-height d-flex align-items-center gap-2 px-4"
              disabled={
                isLoadingData ||
                !cardBrandRows.rows ||
                cardBrandRows.rows.length === 0
              }
              onClick={downloadReport}
              style={{
                borderRadius: '20px',
                borderColor: '#e6e8ec',
                color: '#121923',
                fontWeight: '600',
              }}
            >
              <ExcelIcon />
              <span>Excel</span>
            </button>
          </Stack>
        </Stack>
      </div>

      <Results
        isLoading={isLoadingData}
        fromDate={fromDate}
        toDate={toDate}
        rows={cardBrandRows.rows}
      />
    </section>
  )
}

function Results({ isLoading, rows, fromDate, toDate }) {
  if (!fromDate || !toDate) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Get started by choosing your filters</p>
          <p className="subtitle subtitle--dark">
            Select a date range from the options above. Once you've made your
            selections, your report will appear right away.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (!rows || rows.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No data</p>
          <p className="subtitle subtitle--dark">
            There&apos;s currently no data for the specified criteria. Please
            try different filters.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md mb-4">
      <div className="table-container">
        <div className="table-container--fixed-height--scrollable">
          <Table responsive hover>
            <thead>
              <tr className="md md--scroll">
                <th className="text-left uppercase text-body">Brand</th>
                <th className="text-left uppercase text-body">Total</th>
                <th className="text-left uppercase text-body">Total Revenue</th>
                <th className="text-left uppercase text-body">
                  Percentage Used
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.brand} className="md-x md-x--scroll">
                  <td className="text-left">
                    <div className="brand-cell">
                      <img
                        className="brand-icon"
                        src={cardIcon}
                        alt={`${row.brand} logo`}
                      />
                      <span className="brand-text">{row.brand}</span>
                    </div>
                  </td>
                  <td className="text-left">{formatNumber(row.total)}</td>
                  <td className="text-left">
                    {formatCurrency(row.totalRevenue)}
                  </td>
                  <td className="text-left percentage">
                    {row.percentage.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </Card>
  )
}
